import { useLazyQuery } from '@apollo/client';
import type {
  FetchPhysicalAddressQuery,
  FetchPhysicalAddressQueryVariables,
  SearchPhysicalAddressQuery,
  SearchPhysicalAddressQueryVariables,
} from '@generated/graphql';
import type { PhysicalAddress } from '@interfaces/physicalAddress';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LockIcon from '@mui/icons-material/Lock';
import {
  type AutocompleteInputChangeReason,
  ButtonBase,
  InputAdornment,
  Menu,
  MenuItem,
  type SxProps,
} from '@mui/material';
import type { CountryCodes } from '@onevesthq/ov-enums';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { getCountryName } from '@ovComponents/resources';
import { Tooltip, useToast } from 'hephaestus/web';
import { kebabCase, throttle } from 'lodash-es';
import {
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { JSX } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';
import { Autocomplete } from '../autocomplete/autocomplete';
import {
  FETCH_PHYSICAL_ADDRESS,
  SEARCH_PHYSICAL_ADDRESS,
} from './addressField.queries';
import { dedupeAddressOptionsByIdOrText } from './addressOption.utils';
import {
  type CanadaPostLookupResult,
  DEFAULT_CANADA_POST_COUNTRIES,
  getInitialCountry,
  mapCanadaPostLookupResult,
  resolveLookupCountry,
} from './canadaPostAddress.utils';

type Props = {
  label?: string;
  addressString?: string;
  setAddress?: (address: Partial<PhysicalAddress>) => void;
  onClear?: () => void;
  error?: boolean;
  integrationId?: string;
  organizationId?: string;
  modalOpen: boolean;
  errorText?: string;
  disabled?: boolean;
  onFocus?: any;
  onBlur?: any;
  sx?: SxProps;
  infoTooltip?: string;
  onLookupFault?: () => void;
  lockMessage?: string;
  testId?: string;
  supportedCountries?: CountryCodes[];
  addressCountry?: CountryCodes;
};

type PlaceType = {
  text: string;
  id: string;
  next?: string;
};

const CanadaPostAddress = ({
  setAddress,
  onClear,
  label,
  addressString,
  error,
  integrationId,
  organizationId,
  modalOpen = false,
  errorText,
  disabled,
  onFocus,
  sx,
  onBlur,
  infoTooltip,
  onLookupFault,
  lockMessage,
  testId,
  supportedCountries = DEFAULT_CANADA_POST_COUNTRIES,
  addressCountry,
}: Props): JSX.Element => {
  const { language } = useLocalization();
  const { t } = useTranslation(['pageConfiguration', 'components']);
  const { enqueueToast } = useToast();
  const [value, setValue] = useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = useState<string>(addressString ?? '');
  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const inputRef = useRef<any>(null);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [countryMenuAnchor, setCountryMenuAnchor] =
    useState<HTMLElement | null>(null);
  // Focusing while the menu's focus trap is active would bounce focus back and blur the input.
  const focusInputAfterMenuCloses = useRef(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryCodes>(() =>
    getInitialCountry(supportedCountries, addressCountry),
  );
  const baseTestId = testId ?? 'canada-post-address';

  // A cleared address has no country, which must not reset the country the user just picked.
  useEffect(() => {
    setSelectedCountry((current) =>
      addressCountry || !supportedCountries.includes(current)
        ? getInitialCountry(supportedCountries, addressCountry)
        : current,
    );
  }, [supportedCountries, addressCountry]);

  const changeCountry = (country: CountryCodes) => {
    setCountryMenuAnchor(null);
    if (country === selectedCountry) return;
    setSelectedCountry(country);
    setValue(null);
    setInputValue('');
    setOptions([]);
    setOpen(false);
    if (onClear) onClear();
    focusInputAfterMenuCloses.current = true;
  };

  const [searchAddress] = useLazyQuery<
    SearchPhysicalAddressQuery,
    SearchPhysicalAddressQueryVariables
  >(SEARCH_PHYSICAL_ADDRESS);
  const [fetchAddress] = useLazyQuery<
    FetchPhysicalAddressQuery,
    FetchPhysicalAddressQueryVariables
  >(FETCH_PHYSICAL_ADDRESS);

  const SUPPORTED_PROVINCE: string[] = []; // todo: OV-11858

  const handleLookupError = useCallback(
    (callback: (results?: readonly PlaceType[]) => void) => {
      enqueueToast(
        'Failed to fetch address suggestions. Fallback to manual input.',
        { variant: 'error' },
      );
      if (onLookupFault) onLookupFault();
      callback([]);
    },
    [enqueueToast, onLookupFault],
  );

  const fetchAddressSuggestion = useMemo(
    () =>
      throttle(
        async (
          input: string,
          countryCode: CountryCodes,
          callback: (results?: readonly PlaceType[]) => void,
          lastId?: string,
        ) => {
          if (!integrationId || !organizationId) {
            handleLookupError(callback);
            return;
          }

          try {
            const { data } = await searchAddress({
              variables: {
                input: {
                  integrationId,
                  organizationId,
                  searchTerm: input,
                  language: language,
                  maxResults: 8,
                  countryCode,
                  ...(lastId ? { lastId } : {}),
                },
              },
            });

            if (!data?.searchPhysicalAddress?.suggestions) {
              handleLookupError(callback);
              return;
            }

            const suggestions = data.searchPhysicalAddress.suggestions.map(
              (item) => ({
                id: item.id,
                next: item.next ?? undefined,
                text: item.text,
              }),
            );
            callback(dedupeAddressOptionsByIdOrText(suggestions));
          } catch {
            handleLookupError(callback);
          }
        },
        200,
      ),
    [integrationId, organizationId, searchAddress, handleLookupError],
  );

  useEffect(() => {
    let active = true;
    if (!inputValue) {
      setOptions(value ? [value] : []);
      return undefined;
    }
    if (!hasBeenFocused) return undefined;

    fetchAddressSuggestion(
      inputValue,
      selectedCountry,
      (results?: readonly PlaceType[]) => {
        if (active) {
          let newOptions: readonly PlaceType[] = [];
          if (results) {
            newOptions = [...newOptions, ...results];
          }
          setOptions(dedupeAddressOptionsByIdOrText(newOptions));
        }
      },
      value?.id,
    );
    return () => {
      active = false;
    };
  }, [
    value,
    inputValue,
    fetchAddressSuggestion,
    hasBeenFocused,
    selectedCountry,
  ]);

  const fetchAddressById = async (
    selectedOption: PlaceType,
  ): Promise<CanadaPostLookupResult | null> => {
    if (!integrationId || !organizationId) return null;

    try {
      const { data } = await fetchAddress({
        variables: {
          input: {
            integrationId,
            organizationId,
            addressId: selectedOption.id,
            language: language,
          },
        },
      });
      return data?.fetchPhysicalAddress?.addressDetails ?? null;
    } catch {
      throw new Error('Failed to fetch address details');
    }
  };

  const validateLocationRestriction = async (
    result: CanadaPostLookupResult,
  ): Promise<boolean> => {
    const countryCode = resolveLookupCountry(result);
    const provinceCode = [
      result.Province,
      result.ProvinceCode,
      result.ProvinceCode,
    ];

    if (!supportedCountries.some((x) => x === countryCode)) {
      enqueueToast(t('components:address.error.unSupportedCountry'), {
        variant: 'error',
      });
      return false;
    }

    if (
      (SUPPORTED_PROVINCE ?? []).length > 0 &&
      !SUPPORTED_PROVINCE.some((x) => provinceCode.includes(x))
    ) {
      enqueueToast(t('components:address.error.unSupportedProvince'), {
        variant: 'error',
      });
      return false;
    }

    return true;
  };

  const handleSelect = async (selectedOption: PlaceType): Promise<void> => {
    if (selectedOption.next === 'Find') {
      const regex = / - \d+ Addresses$/;
      const newText = selectedOption.text.replace(regex, '');
      setValue({ text: newText, id: selectedOption.id });
      setInputValue(newText);
      setOpen(true);
      return;
    }
    setValue({ text: selectedOption.text, id: '' });

    try {
      const result = await fetchAddressById(selectedOption);

      if (!result) return;

      const validate: boolean = await validateLocationRestriction(result);

      if (!validate) {
        setInputValue('');
        return;
      }

      const localAddress = mapCanadaPostLookupResult(
        result,
        selectedOption.text,
      );
      if (setAddress) setAddress(localAddress);
      setOpen(false);
    } catch {
      enqueueToast(
        'Failed to fetch address details. Fallback to manual input.',
        { variant: 'error' },
      );
    }
  };

  const fieldValue = modalOpen ? `${value} ` : value;
  const hasCountrySelector = supportedCountries.length > 1;

  // Stops the click from focusing the input and opening the suggestion list.
  const stopInputInteraction = (event: React.SyntheticEvent) =>
    event.stopPropagation();

  const countryPicker = hasCountrySelector ? (
    <InputAdornment position='start' sx={{ mr: 1 }}>
      <ButtonBase
        data-testid={`${baseTestId}-country-picker`}
        aria-label={t('components:address.country')}
        aria-haspopup='menu'
        disabled={disabled}
        onMouseDown={stopInputInteraction}
        onClick={(event) => {
          stopInputInteraction(event);
          setCountryMenuAnchor(event.currentTarget);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          pr: 1,
          borderRight: 1,
          borderColor: 'divider',
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {selectedCountry}
        <ArrowDropDownIcon fontSize='small' />
      </ButtonBase>
      <Menu
        anchorEl={countryMenuAnchor}
        open={!!countryMenuAnchor}
        disableRestoreFocus
        TransitionProps={{
          onExited: () => {
            if (!focusInputAfterMenuCloses.current) return;
            focusInputAfterMenuCloses.current = false;
            inputRef.current?.focus();
          },
        }}
        onClose={() => setCountryMenuAnchor(null)}
        onMouseDown={stopInputInteraction}
      >
        {supportedCountries.map((country) => (
          <MenuItem
            key={country}
            data-testid={`${baseTestId}-country-option-${country.toLowerCase()}`}
            selected={country === selectedCountry}
            onClick={() => changeCountry(country)}
          >
            {getCountryName(country, language)}
          </MenuItem>
        ))}
      </Menu>
    </InputAdornment>
  ) : null;

  return (
    <Autocomplete
      testId={testId ?? 'canada-post-autocomplete'}
      open={open ?? false}
      label={label ?? ''}
      infoTooltip={infoTooltip}
      error={error}
      errorText={errorText}
      disabled={disabled}
      onFocus={() => {
        setHasBeenFocused(true);
        if (onFocus) onFocus();
      }}
      blurOnSelect
      fullWidth
      // X clears the saved address; hidden when there is none.
      disableClearable={!addressString}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.text
      }
      freeSolo
      onBlur={onBlur}
      sx={sx}
      InputProps={{
        inputRef,
        ...(countryPicker ? { startAdornment: countryPicker } : {}),
        // Enabled: native clear-X (freeSolo hides the chevron itself).
        ...(disabled
          ? {
              endAdornment: (
                <InputAdornment position='end'>
                  <Tooltip title={lockMessage ?? t('notEditableMessage')}>
                    <Icon icon={LockIcon} size='small' />
                  </Tooltip>
                </InputAdornment>
              ),
            }
          : {}),
        disabled,
      }}
      options={[...options]}
      value={open ? fieldValue : addressString}
      onChange={(
        _: React.ChangeEvent<HTMLInputElement>,
        newValue: PlaceType | null,
      ) => {
        setValue(newValue);
        if (newValue) {
          handleSelect(newValue);
        }
      }}
      renderOption={(props: any, option: PlaceType) => (
        <li
          {...props}
          data-testid={
            testId
              ? `${testId}-${kebabCase(option.text)}`
              : `canada-post-autocomplete-${kebabCase(option.text)}`
          }
        >
          {option.text}
        </li>
      )}
      onInputChange={(
        _: React.ChangeEvent<HTMLInputElement>,
        newInputValue: SetStateAction<string>,
        reason: AutocompleteInputChangeReason,
      ) => {
        if (reason === 'input') setOpen(!modalOpen);
        setInputValue(newInputValue);
        if ((reason === 'input' || reason === 'clear') && !newInputValue) {
          setValue(null);
          if (onClear) onClear();
        }
      }}
      filterOptions={(x) => x}
    />
  );
};

export default CanadaPostAddress;
